import { Fragment, useMemo } from 'react'
import {
  AuditTableCellText,
  AuditOrangeEmail,
  AuditOrangeText,
  StyledLink as Link,
} from './styles'
import { DomainLogs } from '../types'

interface LogMessageProps {
  log: DomainLogs
  rootDomainId: string
}

const AuditActionCell = ({ log, rootDomainId }: LogMessageProps) => {
  const {
    type,
    subType,
    action,
    homeId,
    domain: { name, id },
  } = log
  const { email, firstName, lastName, enabled = false, expires } = log.data

  const isEnabledConfigAccess = new Date(expires).valueOf() > Date.now()

  /**
   * @todo add localization
   */
  const homeName = useMemo(
    () =>
      homeId && id ? (
        <Link to={`/${rootDomainId}/jobs/${id}/homes/${homeId}`}>
          <AuditOrangeText>Home (id: {homeId})</AuditOrangeText>
        </Link>
      ) : (
        'home'
      ),
    [homeId, id, rootDomainId]
  )

  const jobName = useMemo(
    () =>
      id ? (
        <Link to={`/${rootDomainId}/jobs/${id}`}>
          <AuditOrangeText>{name}</AuditOrangeText>
        </Link>
      ) : (
        `'${name}'`
      ),
    [id, name, rootDomainId]
  )

  /**
   * @todo add localization
   */
  const generateMessage = useMemo(() => {
    switch (type) {
      case 'domain':
        switch (subType) {
          default:
            switch (action) {
              case 'add':
                return `Added a subdomain or job to '${name}'`
              case 'modify':
                return `Modified domain '${name}`
              case 'moved':
                return `Moved a domain or job to '${name}'`
              case 'remove':
                return `Removed a subdomain or job from '${name}'`
              default:
                return `Domain event in '${name}'`
            }
        }
      case 'user':
        switch (subType) {
          default: {
            switch (action) {
              case 'add':
                return (
                  <Fragment>
                    {'Added user '}
                    <AuditOrangeEmail address={email}>
                      {firstName} {lastName}
                    </AuditOrangeEmail>
                  </Fragment>
                )
              case 'modify':
                return (
                  <Fragment>
                    {'Modified user '}
                    <AuditOrangeEmail address={email}>
                      {firstName} {lastName}
                    </AuditOrangeEmail>
                  </Fragment>
                )
              case 'moved':
                return (
                  <Fragment>
                    {'Moved user '}
                    <AuditOrangeEmail address={email}>
                      {firstName} {lastName}
                    </AuditOrangeEmail>
                  </Fragment>
                )
              case 'remove':
                return (
                  <Fragment>
                    {'Removed user '}
                    <AuditOrangeEmail address={email}>
                      {firstName} {lastName}
                    </AuditOrangeEmail>
                  </Fragment>
                )
              default:
                return `User event in '${name}'`
            }
          }
        }
      case 'home_move_request':
        switch (subType) {
          default:
            switch (action) {
              case 'request':
                return (
                  <Fragment>
                    Moved {homeName} to job {jobName}
                  </Fragment>
                )
              default:
                return (
                  <Fragment>
                    Request to move {homeName} to job {jobName}
                  </Fragment>
                )
            }
        }
      case 'home_access':
        switch (subType) {
          default:
            switch (action) {
              case 'monitor_access': {
                return (
                  <Fragment>
                    {enabled ? 'Enabled' : 'Disabled'} monitor access for{' '}
                    {homeName}, job {jobName}
                  </Fragment>
                )
              }
              case 'config_access': {
                return (
                  <Fragment>
                    {isEnabledConfigAccess ? 'Enabled ' : 'Disabled '}
                    config access for {homeName}, job {jobName}
                  </Fragment>
                )
              }
              default:
                return `Access requested for ${homeName}`
            }
        }
      default:
        return `Event in domain '${name}.'`
    }
  }, [
    type,
    subType,
    name,
    action,
    email,
    firstName,
    lastName,
    homeName,
    jobName,
    enabled,
    isEnabledConfigAccess,
  ])

  if (!log) return null
  return <AuditTableCellText>{generateMessage}</AuditTableCellText>
}

export default AuditActionCell
